import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from './layouts/AppShell';

import { ExplorerWorkspace } from './modules/governance/process/docs/pages/ExplorerWorkspace';
import { DocumentViewerPage } from './modules/governance/process/docs/pages/DocumentViewerPage';
import { ProcessSheetPage } from './modules/governance/process/pages/ProcessSheetPage';
import { ImportarPage } from './modules/governance/process/docs/pages/ImportarPage';
import { AuditoriaPage } from './modules/governance/compliance/pages/AuditoriaPage';
import { PersonasPage } from './modules/governance/structure/people/pages/PersonasPage';
// 0609: PersonaDetailPage eliminada (366L) — duplicaba AdminPersonaPanel (modal
// overlay en /structure?tab=administracion). Todos los callsites refactorizados.
// 0651: OrganigramaPage import eliminado tras redirect /structure/organigrama → /structure?tab=organigrama
// (el componente sigue disponible vía StructurePage tabs internamente).
import { OrganizacionesPage } from './modules/governance/structure/organizations/pages/OrganizacionesPage';
import { GobernanzaPage } from './modules/governance/structure/pages/GobernanzaPage';
// 0585: AdminSettingsPage borrado (962L) - reemplazado por /admin (12 tabs PRD-002).
import { AdminPage } from './modules/governance/admin/pages/AdminPage';
import { TenantSelector } from './modules/governance/admin/pages/TenantSelector';
import { OnboardingWizard } from './modules/governance/admin/pages/OnboardingWizard';
import { CrearTenantWizardPage } from './modules/governance/admin/pages/CrearTenantWizardPage';
// Legacy editor removed — editing is now in-situ via DocumentPanel
// import { DocumentEditorPage } from './modules/governance/process/docs/pages/DocumentEditorPage';
import { ImportDocumentPage } from './modules/governance/process/docs/pages/ImportDocumentPage';
import { GlobalStyles } from './lib/ui';
import { ModuleGuard } from './modules/shared/components/ModuleGuard';
import { VIAChatButton } from './modules/platform/ai/VIAChatButton';
import {
  ProcessMapLevel1,
  ProcessMapLevel2,
  ProcessMapLevel3,
} from './modules/governance/process/process-map';
import { StructurePage } from './modules/governance/structure/pages/StructurePage';
import { MadurezProcesoDashboard } from './modules/governance/process/maturity/MadurezProcesoDashboard';
import { PuestoDetailPage } from './modules/governance/structure/positions/pages/PuestoDetailPage';
import { ProcessPage } from './modules/governance/process/pages/ProcessPage';
import { InformationPage } from './modules/governance/process/docs/pages/InformationPage';
import { CompliancePage } from './modules/governance/compliance/pages/CompliancePage';
import { TechPage } from './modules/apps/TechPage';
import { ReputationPage } from './modules/apps/news/pages/ReputationPage';
// 0666b: ActionCenterPage legacy queda en disco como referencia hasta cleanup futuro.
// La ruta /sign/action-center ahora monta ActionCenterPremium (tabla densa virtualizada
// + React Query + detail expand 3 columnas que muestra asunto+alcance del 0665).
import { ActionCenterPremium } from './modules/platform/sign/ActionCenterPremium';
import { PeticionesPage } from './modules/platform/sign/requests/PeticionesPage';
import { ComprasPage } from './modules/platform/sign/requests/ComprasPage';
import { RequestsAdminPage } from './modules/platform/requests/RequestsAdminPage';
import { RequestCreatePage } from './modules/platform/requests/RequestCreatePage';
import { MisSolicitudesPage } from './modules/platform/requests/MisSolicitudesPage';
import { RequestDetailPage } from './modules/platform/requests/RequestDetailPage';
import { OCRIngestPage } from './modules/platform/ocr/OCRIngestPage';
import { GlosarioPage } from './modules/shared/pages/GlosarioPage';
import { LoginPage } from './modules/governance/admin/pages/LoginPage';
import { RequireAuth } from './components/RequireAuth';
import { RequireRole } from './components/RequireRole';
import { Sentry } from './lib/sentry';

export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 16,
            color: '#5A6171',
            fontFamily: 'inherit',
          }}
        >
          <p style={{ fontSize: 14, margin: 0 }}>
            Ha ocurrido un error inesperado. Por favor, recarga la pagina.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
              color: '#1A1D23',
              fontFamily: 'inherit',
            }}
          >
            Recargar
          </button>
        </div>
      }
    >
      <BrowserRouter>
        <GlobalStyles />
        <Routes>
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            {/* Home — redirige al mapa de procesos */}
            <Route path="/" element={<Navigate to="/process-map" replace />} />

            {/* Discipline pages */}
            <Route
              path="/structure"
              element={
                <ModuleGuard modulo="M1">
                  <StructurePage />
                </ModuleGuard>
              }
            />
            <Route
              path="/process"
              element={
                <ModuleGuard modulo="M2">
                  <ProcessPage />
                </ModuleGuard>
              }
            />
            <Route path="/information" element={<InformationPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/tech" element={<TechPage />} />
            <Route
              path="/tech/ocr"
              element={
                <ModuleGuard modulo="M4">
                  <RequireRole
                    roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN']}
                    redirectTo="/"
                  >
                    <OCRIngestPage />
                  </RequireRole>
                </ModuleGuard>
              }
            />
            <Route path="/reputation" element={<ReputationPage />} />

            {/* Glosario — transversal (sin ModuleGuard) */}
            <Route path="/glosario" element={<GlosarioPage />} />

            {/* Sign — infraestructura (sin ModuleGuard) */}
            <Route path="/sign/action-center" element={<ActionCenterPremium />} />
            <Route path="/sign/peticiones" element={<PeticionesPage />} />
            <Route path="/sign/compras" element={<ComprasPage />} />

            {/* Requests — M16 */}
            <Route
              path="/requests/admin"
              element={
                <ModuleGuard modulo="M16">
                  <RequireRole
                    roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN']}
                    redirectTo="/"
                  >
                    <RequestsAdminPage />
                  </RequireRole>
                </ModuleGuard>
              }
            />
            <Route
              path="/requests/nueva"
              element={
                <ModuleGuard modulo="M16">
                  <RequestCreatePage />
                </ModuleGuard>
              }
            />
            <Route
              path="/requests/mis"
              element={
                <ModuleGuard modulo="M16">
                  <MisSolicitudesPage />
                </ModuleGuard>
              }
            />
            <Route
              path="/requests/:id"
              element={
                <ModuleGuard modulo="M16">
                  <RequestDetailPage />
                </ModuleGuard>
              }
            />

            {/* Structure — M1 */}
            {/* 0651: redirect ruta standalone → tab embebido (cierra bug doble cabecera item BACKLOG [0615] L81+L87).
                La ruta `/structure?tab=organigrama` (StructurePage) renderiza `OrganigramaPage` con
                `embedded=true` → solo AppShell global, sin AppHeader propio duplicado. */}
            <Route
              path="/structure/organigrama"
              element={<Navigate to="/structure?tab=organigrama" replace />}
            />
            <Route
              path="/structure/organizaciones"
              element={
                <ModuleGuard modulo="M1">
                  <OrganizacionesPage />
                </ModuleGuard>
              }
            />
            <Route
              path="/structure/gobernanza"
              element={
                <ModuleGuard modulo="M1">
                  <GobernanzaPage />
                </ModuleGuard>
              }
            />

            {/* Process — M2 */}
            <Route
              path="/process/explorador"
              element={
                <ModuleGuard modulo="M2">
                  <ExplorerWorkspace />
                </ModuleGuard>
              }
            />
            {/* 0735 F3: M2.7 Madurez de Procesos (BPMM global) */}
            <Route
              path="/process/madurez"
              element={
                <ModuleGuard modulo="M2">
                  <MadurezProcesoDashboard />
                </ModuleGuard>
              }
            />
            <Route path="/process/mapa" element={<Navigate to="/process-map" replace />} />

            {/* Process Map — M2 (Niveles 1, 2, 3) */}
            <Route
              path="/process-map"
              element={
                <ModuleGuard modulo="M2">
                  <ProcessMapLevel1 />
                </ModuleGuard>
              }
            />
            <Route
              path="/process-map/group/:groupId"
              element={
                <ModuleGuard modulo="M2">
                  <ProcessMapLevel2 />
                </ModuleGuard>
              }
            />
            <Route
              path="/process-map/process/:processId"
              element={
                <ModuleGuard modulo="M2">
                  <ProcessMapLevel3 />
                </ModuleGuard>
              }
            />

            {/* Documents — sin guard (acceso por RBAC) */}
            <Route path="/documento/:codigo" element={<DocumentViewerPage />} />
            {/* Legacy editor route — replaced by in-situ editing in DocumentPanel */}
            {/* <Route path="/documento/:codigo/editar" element={<DocumentEditorPage />} /> */}
            <Route path="/proceso/:codigo/ficha" element={<ProcessSheetPage />} />
            {/* Acceso directo por código V·IAnet desde V·IA Chat (ej: /proceso/PO.74) */}
            <Route
              path="/proceso/:processId"
              element={
                <ModuleGuard modulo="M2">
                  <ProcessMapLevel3 />
                </ModuleGuard>
              }
            />

            {/* Information — M7 */}
            <Route
              path="/information/auditoria"
              element={
                <ModuleGuard modulo="M7">
                  <AuditoriaPage />
                </ModuleGuard>
              }
            />

            {/* V·IA Personas — M1 Structure */}
            <Route
              path="/structure/personas"
              element={
                <ModuleGuard modulo="M1">
                  <PersonasPage />
                </ModuleGuard>
              }
            />
            {/* 0609: ruta /structure/admin/personas/:id ELIMINADA — la persona
                se gestiona ahora desde el modal AdminPersonaPanel embebido en
                /structure?tab=administracion (deep-link via &personaId=X). */}
            <Route
              path="/structure/puestos/:id"
              element={
                <ModuleGuard modulo="M1">
                  <PuestoDetailPage />
                </ModuleGuard>
              }
            />
            {/* Legacy redirects */}
            <Route path="/apps/personas" element={<Navigate to="/structure/personas" replace />} />

            {/* Admin — M4 para importar, settings sin guard */}
            <Route
              path="/admin/importar"
              element={
                <ModuleGuard modulo="M4">
                  <RequireRole
                    roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN']}
                    redirectTo="/"
                  >
                    <ImportarPage />
                  </RequireRole>
                </ModuleGuard>
              }
            />
            <Route
              path="/documentos/importar"
              element={
                <ModuleGuard modulo="M4">
                  <RequireRole
                    roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN']}
                    redirectTo="/"
                  >
                    <ImportDocumentPage />
                  </RequireRole>
                </ModuleGuard>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireRole
                  roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_AUDITOR']}
                  redirectTo="/"
                >
                  <AdminPage />
                </RequireRole>
              }
            />
            {/* 0585: redirect legacy /admin/settings -> /admin (12 tabs PRD-002) */}
            <Route path="/admin/settings" element={<Navigate to="/admin" replace />} />
            <Route
              path="/admin/:seccion"
              element={
                <RequireRole
                  roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_AUDITOR']}
                  redirectTo="/"
                >
                  <AdminPage />
                </RequireRole>
              }
            />
            <Route
              path="/admin/:seccion/:sub"
              element={
                <RequireRole
                  roles={['SUPERADMIN_PLATFORM', 'TENANT_OWNER', 'TENANT_ADMIN', 'TENANT_AUDITOR']}
                  redirectTo="/"
                >
                  <AdminPage />
                </RequireRole>
              }
            />
            {/* /backoffice eliminado en 0566 — ahora es tab dentro de /admin/backoffice */}
            <Route path="/backoffice" element={<Navigate to="/admin/backoffice" replace />} />
            <Route
              path="/backoffice/tenants/crear"
              element={
                <RequireRole roles={['SUPERADMIN_PLATFORM']} redirectTo="/">
                  <CrearTenantWizardPage />
                </RequireRole>
              }
            />

            {/* Legacy routes */}
            <Route
              path="/auditoria"
              element={
                <ModuleGuard modulo="M7">
                  <AuditoriaPage />
                </ModuleGuard>
              }
            />
          </Route>

          {/* Fullscreen pages (outside AppShell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/tenant-selector" element={<TenantSelector />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
        </Routes>
        <VIAChatButton />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
